import structuredClone from "@ungap/structured-clone";
import type { Option } from "@waldner-laboreinrichtungen/component-system";
import {
  Dropdown,
  FilterItem,
  FlexBox,
  space,
  Table,
  Tag,
  theme,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import type { FC, MutableRefObject } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import styledComponents from "styled-components";

import { ReactComponent as InfoIcon } from "../../public/material-icons/info.svg";
import { ReactComponent as TimelapseIcon } from "../../public/material-icons/timelapse.svg";
import { useAccessToken } from "../auth-provider";
import { useDeviceImage } from "../hooks/use-device-image";
import {
  resolveIncidentDevice,
  updateIncidentStates,
} from "../hooks/use-incidents";
import { useUnitStore } from "../store/unit-store";
import {
  formatDateWithSuffix,
  getTimeDifference,
} from "../util/time-formatter";
import type { Device, Incident, SubClass } from "../view-model";
import { ErrorReportModal } from "./error-report-modal";

export type IncidentWithDevice = Incident & {
  device?: Device;
};

export type ErrorHistory = {
  className?: string;
  devices: Device[];
  incidents: IncidentWithDevice[];
  loadMoreComponent?: MutableRefObject<HTMLTableRowElement>;
  isLoadingMore: boolean;
  // Weitere Server-Seiten vorhanden? Steuert den Infinite-Scroll-Sentinel
  // unabhängig von der (client-gefilterten) Zeilenzahl.
  hasMore?: boolean;
  selectedStatus?: Option & { emptyText?: string };
  setSelectedStatus?: (status: Option & { emptyText?: string }) => void;
};

// History-Zeile zeigt einen Tag (am weitesten fortgeschrittener Zustand).
// Priorität: Behoben > Bestätigt > Quittiert > Gemeldet.
const HistoryStateTag: FC<{ incident: Incident }> = ({ incident }) => {
  const { t } = useTranslation();
  let stateKey: string | null = null;
  if (incident.active === false) {
    stateKey = "incident.state.fixed";
  } else if (incident.confirmed) {
    stateKey = "incident.state.confirmed";
  } else if (incident.acknowledged) {
    stateKey = "incident.state.acknowledged";
  } else if (incident.reported) {
    stateKey = "incident.state.reported";
  }
  return stateKey ? <Tag variant="info">{t(stateKey)}</Tag> : null;
};

const IMAGE_WIDTH = "50px";
const IMAGE_HEIGHT = "50px";

const StyledImage = styledComponents.img`
  width: ${IMAGE_WIDTH};
  height: ${IMAGE_HEIGHT};
  object-fit: contain;
  margin-right: ${space(4)};
`;

// Prefer the OPC-UA DeviceTypeImage, fall back to the static device.image, and
// to the generic no-image placeholder when neither exists (or the URL is
// broken) — so devices without an image show the placeholder, not a broken-link
// icon, consistent with the other pages.
const NO_IMAGE = "/devices/no-image.png";
const DeviceImage: FC<{ device?: Device }> = ({ device }) => {
  const src = useDeviceImage(
    device?.deviceTypeImage,
    device?.image || NO_IMAGE
  );
  return (
    <StyledImage
      onError={(e) => {
        if (!e.currentTarget.src.endsWith(NO_IMAGE))
          e.currentTarget.src = NO_IMAGE;
      }}
      src={src || NO_IMAGE}
    />
  );
};

const StyledInfo = styledComponents.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 50%;
  margin-right: ${space(10)};
`;

const StyledErrorHistory = styledComponents.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${space(8)};
`;

type FilterMap = {
  [key in SubClass | "all"]?: {
    value: SubClass | "all";
    displayName: string;
    count: number;
  };
};

const useFilteredDevices = (devices: Device[]) => {
  const { t } = useTranslation();
  const [filteredDevices, setFilteredDevices] = useState(devices);
  const [selectedCategory, setSelectedCategory] = useState<SubClass | "all">(
    "all"
  );

  const deviceCategories = new Set(devices?.map((device) => device.subclass));

  const filterCategories: (SubClass | "all")[] = [
    "all",
    ...deviceCategories.values(),
  ];

  const filterMap: FilterMap = {};
  for (const filter of filterCategories) {
    filterMap[filter] = {
      value: filter,
      displayName: t(`subclass.${filter}`),
      count: 0,
    };
  }

  if (devices) {
    for (const device of devices) {
      // filtermap created above, always non null
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      filterMap["all"]!.count = filterMap["all"]!.count + 1;
      if (device.subclass) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        filterMap[device.subclass]!.count =
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          filterMap[device.subclass]!.count + 1;
      }
    }
  }

  const onChangeFilteredDevices = (filterValue: string) => {
    setSelectedCategory(filterValue as SubClass | "all");
    const newFilteredDevices = devices?.filter((device) => {
      if (filterValue === "all") return true;
      return device.subclass === filterValue;
    });
    setFilteredDevices(newFilteredDevices);
  };

  const filters = Object.entries(filterMap).map(([, filter]) => filter);

  return {
    filteredDevices,
    selectedCategory,
    onChangeFilteredDevices,
    filters,
  };
};

const LoadingSkeletons = Array.from({ length: 3 }).map((_element, index) => (
  // eslint-disable-next-line react/no-array-index-key
  <Table.Row key={index}>
    <Table.Data>
      <Skeleton count={2} />
    </Table.Data>
    <Table.Data>
      <Skeleton />
    </Table.Data>
    <Table.Data>
      <Skeleton />
    </Table.Data>
    <Table.Data>
      <Skeleton />
    </Table.Data>
    <Table.Data>
      <Skeleton count={2} />
    </Table.Data>
    <Table.Data>
      <Skeleton count={2} />
    </Table.Data>
  </Table.Row>
));

export const ErrorHistory: FC<ErrorHistory> = ({
  devices,
  hasMore,
  incidents,
  isLoadingMore,
  loadMoreComponent,
  selectedStatus: selectedStatusProp,
  setSelectedStatus: setSelectedStatusProp,
}) => {
  const { i18n, t } = useTranslation();
  const token = useAccessToken();
  // Mount-aware Zuordnung: Incidents kommen kanonisch unter der QUELLE; ein
  // Monitored Device „besitzt" sie über seine (gemounteten) Channels/Units.
  const getUnits = useUnitStore((s) => s.getUnitsForDevice);
  // Lokale Kopie für optimistisches Update nach einer Aktion (wie DeviceDetails);
  // die Prop-Liste wird bei jedem Fetch nachgezogen.
  const [rows, setRows] = useState(incidents);
  useEffect(() => setRows(incidents), [incidents]);
  const typeOptions: Option[] = [
    {
      id: 0,
      label: t("incident.type.all"),
      value: "all",
    },
    {
      id: 1,
      label: t("incident.type.critical"),
      value: "critical",
    },
    {
      id: 2,
      label: t("incident.type.warning"),
      value: "warning",
    },
  ];

  const statusOptions: (Option & { emptyText?: string })[] = [
    {
      id: 0,
      label: t("incident.status.all"),
      value: "all",
      emptyText: t("incident.empty.all") as string,
    },
    {
      id: 1,
      label: t("incident.status.active"),
      value: "open",
      emptyText: t("incident.empty.active") as string,
    },
    {
      id: 3,
      label: t("incident.state.reported"),
      value: "reported",
      emptyText: t("incident.empty.reported") as string,
    },
  ];

  const [selectedType, setSelectedType] = useState(typeOptions[0]);
  const [selectedStatusLocal, setSelectedStatusLocal] = useState(
    statusOptions[0]
  );

  // Use prop if provided, otherwise use local state
  const selectedStatus = selectedStatusProp || selectedStatusLocal;
  const setSelectedStatus = setSelectedStatusProp || setSelectedStatusLocal;
  const [isOpenModal, setIsOpenModal] = useState(false);

  const {
    filteredDevices,
    filters,
    onChangeFilteredDevices,
    selectedCategory,
  } = useFilteredDevices(devices);

  const filteredIncidents = useMemo(() => {
    const incidentsWithDevice = rows.map((incident) => {
      const IncidentWithDevice: IncidentWithDevice = structuredClone(incident);
      IncidentWithDevice.device = resolveIncidentDevice(
        incident,
        devices,
        getUnits
      );
      return IncidentWithDevice;
    });

    // Filter by type (severity) - still done client-side as API doesn't have this filter
    const incidentsByType = incidentsWithDevice?.filter((incident) => {
      if (selectedType.value === "all") return true;
      return selectedType.value === incident.severity;
    });

    // Status filtering is done via API. The device-category tab filters by
    // subclass — but "all" must keep EVERY incident, incl. ones whose device
    // resolves to none. Match on the mount-aware resolved device (owner), so a
    // Monitored Device's incident is kept under its category, not the raw source.
    const incidentsByDeviceType = incidentsByType.filter((incident) => {
      if (selectedCategory === "all") return true;
      return (
        !!incident.device &&
        filteredDevices.some((device) => device.id === incident.device!.id)
      );
    });
    return incidentsByDeviceType;
  }, [
    selectedType,
    selectedCategory,
    rows,
    filteredDevices,
    devices,
    getUnits,
  ]);

  const [selectedIncident, setSelectedIncident] =
    useState<IncidentWithDevice | null>(filteredIncidents[0] || null);

  const openModal = (incident: IncidentWithDevice) => {
    setSelectedIncident(incident);
    setIsOpenModal(true);
  };

  const updateHistoryIncidentStates = async (
    incident: Incident,
    device: Device,
    action: "ack" | "confirm" | "report",
    comment?: string
  ) => {
    await updateIncidentStates({
      incident,
      device,
      action,
      comment,
      token: token ?? "",
    });
    const flagKey = {
      ack: "acknowledged",
      confirm: "confirmed",
      report: "reported",
    }[action];
    setRows((prev) =>
      prev.map((i) =>
        i.id === incident.id
          ? { ...i, [flagKey]: true, comment: comment ?? i.comment }
          : i
      )
    );
  };

  return (
    <StyledErrorHistory>
      {selectedIncident && (
        <ErrorReportModal
          device={selectedIncident.device}
          incident={selectedIncident as Incident}
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          updateIncidentStates={updateHistoryIncidentStates}
        />
      )}
      <FlexBox justifyContent="space-between">
        <FlexBox gap={space(3)}>
          {filters.map((filter) => (
            <FilterItem
              defaultChecked={filter.value === "all"}
              id={filter.value}
              key={filter.value}
              name="deviceFilter"
              onChange={(e) => onChangeFilteredDevices(e.target.value)}
              value={filter.value}
            >
              {filter.displayName}
            </FilterItem>
          ))}
        </FlexBox>
        <FlexBox gap={space(4)}>
          <Dropdown
            options={typeOptions}
            selectedOption={selectedType}
            setSelectedOption={setSelectedType}
          />
          <Dropdown
            options={statusOptions}
            selectedOption={selectedStatus}
            setSelectedOption={setSelectedStatus}
          />
        </FlexBox>
      </FlexBox>

      <Table
        emptyState={
          !filteredIncidents || filteredIncidents.length === 0 ? (
            <FlexBox
              alignItems="center"
              gap={space(2)}
              height="100%"
              justifyContent="center"
              position="absolute"
              width="100%"
            >
              <InfoIcon color={theme.color.primaryLight} fontSize={30} />
              <Typography size="lg" weight="semiBold">
                {selectedStatus.emptyText}
              </Typography>
            </FlexBox>
          ) : null
        }
      >
        <Table.Head>
          <Table.Row>
            <Table.Header>{t(`header.device`)}</Table.Header>
            <Table.Header columnWidth="100px">
              {t(`header.severity`)}
            </Table.Header>
            <Table.Header columnWidth="100px">
              {t(`header.status`)}
            </Table.Header>
            <Table.Header columnWidth="100px">
              {t(`header.errorcode`)}
            </Table.Header>
            <Table.Header>{t(`header.message`)}</Table.Header>
            <Table.Header columnWidth="200px">{t(`header.date`)}</Table.Header>
            <Table.Header columnWidth="100px" />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {filteredIncidents?.map((incident) => (
            <Table.Row key={incident.id} onClick={() => openModal(incident)}>
              <Table.Data>
                <FlexBox>
                  <DeviceImage device={incident.device} />
                  <StyledInfo>
                    <Typography color="black" size="sm" weight="semiBold">
                      {incident.device?.name || t("data.missing.name")}
                    </Typography>
                    <Typography color="grey400" size="sm">
                      {`${incident.device?.location.position || ""}`}
                    </Typography>
                  </StyledInfo>
                </FlexBox>
              </Table.Data>
              <Table.Data>
                <Tag
                  variant={
                    incident.severity === "critical" ? "error" : "warning"
                  }
                >
                  {t(`incident.type.${incident.severity}`)}
                </Tag>
              </Table.Data>
              <Table.Data>
                {incident.status && incident.status !== "closed" && (
                  <Tag minor variant="neutral">
                    {t(`incident.status.active`)}
                  </Tag>
                )}
              </Table.Data>
              <Table.Data>{incident.code}</Table.Data>
              <Table.Data>
                {i18n.exists(`${incident.message}`, { ns: "incidents" })
                  ? t(`${incident.message}`, { ns: "incidents" })
                  : incident.message || t("unknown.message")}
              </Table.Data>
              <Table.Data>
                {formatDateWithSuffix(new Date(incident.createdAt))}
                <FlexBox
                  alignItems="center"
                  color={theme.colorPalette.green500}
                  gap={space(1)}
                >
                  <TimelapseIcon />
                  <Typography color="grey500" size="xs">
                    {incident.closedAt
                      ? getTimeDifference(
                          new Date(incident.createdAt),
                          new Date(incident.closedAt)
                        )
                      : getTimeDifference(
                          new Date(incident.createdAt),
                          new Date()
                        )}
                  </Typography>
                </FlexBox>
              </Table.Data>
              <Table.Data>
                <FlexBox justifyContent="flex-end">
                  <HistoryStateTag incident={incident} />
                </FlexBox>
              </Table.Data>
            </Table.Row>
          ))}
          {isLoadingMore && filteredIncidents.length > 0 && LoadingSkeletons}
          {/* Sentinel auch bei leer-gefilterter Seite rendern, solange es
              weitere Server-Seiten gibt — sonst bleibt Infinite-Scroll stehen
              und die Historie bleibt fälschlich leer. */}
          {(hasMore || filteredIncidents.length > 0) && (
            <tr ref={loadMoreComponent} />
          )}
        </Table.Body>
      </Table>
    </StyledErrorHistory>
  );
};
